import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { PlugZapIcon } from 'lucide-react';
import { toast } from 'sonner';

import { useExecutionImages } from '@/features/execution-images';
import type {
  ConnectionProviderView,
  ConnectionView,
} from '@/lib/api/generated/model';
import { ListCard } from '@/shared/components/ListCard';
import { LIST_ROWS } from '@/shared/components/listRow';
import { PagePlaceholder } from '@/shared/components/PagePlaceholder';

import { isConnectionError } from '../errors';
import { useConnectionCatalog } from '../hooks/useConnectionCatalog';
import { useConnections } from '../hooks/useConnections';
import { useDeleteConnection } from '../hooks/useDeleteConnection';
import { useUpdateConnection } from '../hooks/useUpdateConnection';
import { connectionKeys } from '../queryKeys';
import { AddConnectionMenu } from './AddConnectionMenu';
import { AvailableRow } from './AvailableRow';
import { ConnectSheet } from './ConnectSheet';
import { ConnectedRow } from './ConnectedRow';
import { CreateConnectionSheet } from './CreateConnectionSheet';
import { DeleteConnectionDialog } from './DeleteConnectionDialog';

interface Opening {
  provider: ConnectionProviderView;
  connection?: ConnectionView;
  replaceToken?: boolean;
}

/**
 * The workspace's connections as two list cards (spec 229 US1): what is
 * connected, each row with its health and a row menu, and what could be,
 * each row with a ghost Connect link. The page's one filled action is the
 * Add connection menu top right (FR-004).
 *
 * Registries are ordinary Connected rows (FR-006), so the enable/disable,
 * the delete and the "still used by an image" guard that lived in the
 * Settings panel live here now. A connection owns its credentials, so
 * nothing here references a secret made elsewhere.
 *
 * A failed load must never render as an empty catalog — same rule
 * `SecretsPage` documents: "you have none" would invite a duplicate the
 * backend then refuses.
 */
export function ConnectionsPage({
  workspaceId,
  createRequested = false,
  onCreateDismissed,
}: {
  workspaceId: string;
  /** The address asked to add a connection (spec 128); spent as it is honoured. */
  createRequested?: boolean;
  onCreateDismissed?: () => void;
}) {
  const queryClient = useQueryClient();
  const { connections, isPending, loadFailed, refetch } =
    useConnections(workspaceId);
  const catalog = useConnectionCatalog(workspaceId);
  const { images } = useExecutionImages(workspaceId);
  const update = useUpdateConnection(workspaceId);
  const remove = useDeleteConnection(workspaceId);

  const [connecting, setConnecting] = useState<Opening | null>(null);
  const [addingMulti, setAddingMulti] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<{
    connection: ConnectionView;
    provider?: ConnectionProviderView;
  } | null>(null);

  const providers = catalog.providers ?? [];
  const multiProviders = catalog.multiProviders ?? [];
  const all = connections ?? [];
  const byType = new Map(
    [...providers, ...multiProviders].map((provider) => [
      provider.type,
      provider,
    ]),
  );
  const multiTypes = new Set(multiProviders.map((provider) => provider.type));

  // Connected: the single-instance providers in catalogue order, then every
  // registry in list order, then whatever the catalogue no longer describes.
  const connected: {
    connection: ConnectionView;
    provider?: ConnectionProviderView;
  }[] = [
    ...providers.flatMap((provider) => {
      const connection = all.find((c) => c.type === provider.type);
      return connection ? [{ connection, provider }] : [];
    }),
    ...all
      .filter((c) => multiTypes.has(c.type))
      .map((connection) => ({
        connection,
        provider: byType.get(connection.type),
      })),
    ...all
      .filter((c) => !byType.has(c.type))
      .map((connection) => ({ connection, provider: undefined })),
  ];
  const connectedCount = connected.filter(
    (item) => item.provider !== undefined,
  ).length;

  const unconnected = providers.filter(
    (provider) => !all.some((c) => c.type === provider.type),
  );
  // Registries are always offered: another can be added at any time.
  const available = [
    ...unconnected.map((provider) => ({ provider, another: false })),
    ...multiProviders.map((provider) => ({
      provider,
      another: all.some((c) => c.type === provider.type),
    })),
  ];
  const addable = available.map((item) => item.provider);

  // The address's request opens the first provider still to connect, once the
  // catalog is in. Derived rather than set from an effect, so it holds from the
  // first render the catalog is there.
  const [addRequested, setAddRequested] = useState(createRequested);
  const requested: Opening | null =
    addRequested && unconnected[0] ? { provider: unconnected[0] } : null;
  const open = connecting ?? requested;

  // Spend the address's request once honoured, so a reload does not open the
  // sheet again (the board's `?create` rule).
  useEffect(() => {
    if (createRequested) onCreateDismissed?.();
  }, [createRequested, onCreateDismissed]);

  // Client-side only (research.md D4 of the registries spec): a registry a
  // catalog entry still pulls from cannot be removed, since removing it would
  // leave that entry's image un-pullable.
  const inUseConnectionIds = new Set(
    (images ?? [])
      .map((image) => image.connection_id)
      .filter((id): id is string => id !== null),
  );

  function toggleEnabled(connection: ConnectionView) {
    const nextEnabled = !connection.enabled;
    update.mutate(
      { connectionId: connection.id, data: { enabled: nextEnabled } },
      {
        onSuccess: () => {
          toast.success(
            nextEnabled
              ? `Enabled ${connection.name}.`
              : `Disabled ${connection.name}.`,
          );
        },
        onError: (error) => {
          toast.error(
            isConnectionError(error)
              ? error.message
              : `${connection.name} could not be updated.`,
          );
        },
      },
    );
  }

  function requestDisconnect(
    connection: ConnectionView,
    provider?: ConnectionProviderView,
  ) {
    if (inUseConnectionIds.has(connection.id)) {
      toast.error(
        `${connection.name} is still used by an execution image and cannot be deleted.`,
      );
      return;
    }
    remove.reset();
    setPendingDelete({ connection, provider });
  }

  function handleDisconnect() {
    if (!pendingDelete) return;
    const { name } = pendingDelete.connection;
    remove.mutate(pendingDelete.connection.id, {
      onSuccess: () => {
        setPendingDelete(null);
        toast.success(`Disconnected ${name}.`);
      },
    });
  }

  // A refusal keeps the dialog open with the service's reason in it (spec 129
  // FR-005), rather than closing it and toasting.
  const deleteError =
    remove.error === null
      ? null
      : isConnectionError(remove.error)
        ? remove.error.message
        : `${pendingDelete?.connection.name ?? 'It'} could not be disconnected.`;

  const loading = isPending || catalog.isPending;
  const failed = !loading && (loadFailed || catalog.loadFailed);
  const connectedState = loading
    ? 'loading'
    : failed
      ? 'failed'
      : connected.length === 0
        ? 'empty'
        : 'idle';
  const availableState = loading
    ? 'loading'
    : failed
      ? 'failed'
      : available.length === 0
        ? 'empty'
        : 'idle';

  return (
    <PagePlaceholder
      eyebrow="Workspace"
      title="Connections"
      description="Connect a provider once: its token becomes available to triggers and to every agent's CLI, with no secrets to name by hand."
      icon={PlugZapIcon}
      actions={
        <AddConnectionMenu
          providers={addable}
          onSelect={(provider) => {
            if (provider.multi_instance) setAddingMulti(provider.type);
            else setConnecting({ provider });
          }}
        />
      }
    >
      <div className="flex w-full flex-col gap-4">
        <ListCard
          title="Connected"
          count={loading || failed ? undefined : connectedCount}
          noun={['connection', 'connections']}
          shown={connected.length}
          total={connected.length}
          state={connectedState}
          failedText="The connections could not be loaded."
          onRetry={() => void refetch()}
          empty={{
            icon: PlugZapIcon,
            title: 'Nothing connected yet',
            description:
              'Connect a provider and its token reaches triggers and every agent’s CLI.',
          }}
        >
          <ul aria-label="Connected" className={LIST_ROWS}>
            {connected.map(({ connection, provider }) => (
              <ConnectedRow
                key={connection.id}
                workspaceId={workspaceId}
                connection={connection}
                provider={provider}
                onTest={() => {
                  void queryClient.invalidateQueries({
                    queryKey: connectionKeys.health(workspaceId, connection.id),
                  });
                }}
                onEdit={() => {
                  if (provider) setConnecting({ provider, connection });
                }}
                onFix={() => {
                  if (provider) {
                    setConnecting({ provider, connection, replaceToken: true });
                  }
                }}
                onToggleEnabled={
                  multiTypes.has(connection.type)
                    ? () => {
                        toggleEnabled(connection);
                      }
                    : undefined
                }
                onDisconnect={() => {
                  requestDisconnect(connection, provider);
                }}
              />
            ))}
          </ul>
        </ListCard>

        <ListCard
          title="Available"
          count={loading || failed ? undefined : available.length}
          noun={['provider', 'providers']}
          shown={available.length}
          total={available.length}
          state={availableState}
          failedText="The catalogue could not be loaded."
          empty={{
            description: 'Every provider the catalogue offers is connected.',
          }}
        >
          <ul aria-label="Available" className={LIST_ROWS}>
            {available.map(({ provider, another }) => (
              <AvailableRow
                key={provider.type}
                provider={provider}
                linkLabel={another ? 'Add another' : 'Connect'}
                onConnect={() => {
                  if (provider.multi_instance) setAddingMulti(provider.type);
                  else setConnecting({ provider });
                }}
              />
            ))}
          </ul>
        </ListCard>
      </div>

      {open ? (
        <ConnectSheet
          key={`${open.provider.type}:${open.connection?.id ?? 'new'}`}
          workspaceId={workspaceId}
          provider={open.provider}
          connection={open.connection}
          replaceToken={open.replaceToken ?? false}
          open
          deleteBlockedReason={
            open.connection && inUseConnectionIds.has(open.connection.id)
              ? `${open.connection.name} is still used by an execution image and cannot be deleted.`
              : undefined
          }
          onClose={() => {
            setConnecting(null);
            setAddRequested(false);
          }}
        />
      ) : null}

      {addingMulti ? (
        <CreateConnectionSheet
          workspaceId={workspaceId}
          open
          providerType="oci_registry"
          onCreated={() => undefined}
          onClose={() => {
            setAddingMulti(null);
          }}
        />
      ) : null}

      {pendingDelete ? (
        <DeleteConnectionDialog
          connectionName={pendingDelete.connection.name}
          open
          isPending={remove.isPending}
          error={deleteError}
          verb="disconnect"
          exposedEnv={pendingDelete.provider?.exposed_env}
          triggerProfileCount={pendingDelete.connection.trigger_profile_count}
          onConfirm={handleDisconnect}
          onCancel={() => {
            setPendingDelete(null);
            remove.reset();
          }}
        />
      ) : null}
    </PagePlaceholder>
  );
}
